import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "react-bootstrap";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import {
  createSymptomMedicineMappingAction,
  updateSymptomMedicineMappingAction
} from "../../../store/actions/SymptomMedicineMappingAction";
import { getSymptomsAction } from "../../../store/actions/SymptomAction";
import { getMedicinesAction } from "../../../store/actions/MedicinesAction";
import { showErrorAlert } from "../../../services/ErrorHandlingService";
import Loader from "../Common/Loader";

const AddSymptomMedicineMapping = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const isEdit = location.state?.mapping;

  // Sample data for symptoms and medicines
  const symptoms = useSelector((state) => state.symptoms.symptoms || [""]);
  const medicines = useSelector((state) => state.medicines.medicines || []);
  const admin = useSelector((state) => state.auth.admin);
  const admin_id = admin?.admin_id;  // or whatever field holds the ID

  useEffect(() => {
    // dispatch(getRoleListAction());
    dispatch(getMedicinesAction(false));
    dispatch(getSymptomsAction());
  }, [dispatch]);

  // Initialize state
  const [mapping, setMapping] = useState({
    admin_id: admin_id || "",
    symptom_id: "",
    medicine_id: "",
    status: "",
    symptom_medicine_id: null
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [errors, setErrors] = useState("");
  // If in edit mode, populate the form with existing data
  useEffect(() => {
    if (isEdit) {
      setFetching(true);
      setErrors("");
      console.log("data:", isEdit);
      setMapping({
        admin_id: admin_id,
        symptom_id: isEdit.symptom_id || "",
        //symptom_names: isEdit.symptom_name.split("|"),
        medicine_id: isEdit.medicine_id || "",
        status: isEdit.status || "Active",
        symptom_medicine_id: isEdit.symptom_medicine_id
      });
      setFetching(false);
    }
  }, [isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setMapping({
      ...mapping,
      [name]: value,
    });
  };

  // const handleSymptomChange = (selectedOptions) => {
  //   handleChange({
  //     target: {
  //       name: "symptom_names",
  //       value: selectedOptions ? selectedOptions.map((opt) => opt.value) : "",
  //     },
  //   });
  // };
  const validate = () => {
    const newErrors = {};

    if (!mapping.symptom_id.trim()) {
      newErrors.symptom_id = "Select symptom name";
    }

    if (!mapping.medicine_id.trim()) {
      newErrors.medicine_id = "Select medicine name";
    }

    if (!mapping.status) {
      newErrors.status = "Status is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors("");

    if (!validate()) {
      setLoading(false);
      return;
    }
    // Basic validation
    if (!mapping.symptom_id || !mapping.medicine_id) {
      showErrorAlert("Error", "Please fill all required fields");
      setLoading(false); // stop loader if validation fails
      return;
    }

    try {
      if (isEdit) {
        await dispatch(updateSymptomMedicineMappingAction(mapping, navigate));
      } else {
        await dispatch(createSymptomMedicineMappingAction(mapping, navigate));
      }
    } catch (error) {
      console.error("Submit failed:", error);
      showErrorAlert("Error", "Something went wrong. Please try again.");
    } finally {
      setLoading(false); // ensure loader stops in both success/fail
    }
  };

  if (loading || fetching) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
        }}
      >
        <Loader size="lg" />
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-xl-12">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between mb-3">
              <h4>{isEdit ? "Edit" : "Add"} Symptom-Medicine Mapping</h4>
              <Button variant="primary" onClick={() => navigate("/symptom-medicine-mapping")}>
                Back
              </Button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-xl-12 mb-3">
                  <label className="form-label">Symptom Name <span className="text-danger">*</span>
                  </label>
                  <Select
                    name="symptom_id"
                    options={symptoms.map((d) => ({
                      value: String(d.symptom_id),    // ← use id
                      label: d.symptom_name,
                    }))}
                    value={symptoms
                      .map((d) => ({
                        value: String(d.symptom_id),
                        label: d.symptom_name,
                      }))
                      .find((d) => String(d.value) === mapping.symptom_id)}   // ← match by id
                    onChange={(selectedOption) =>
                      setMapping({
                        ...mapping,
                        symptom_id: selectedOption.value,     // ← store id
                      })
                    }
                    placeholder="Select symptom"
                    //required
                  />
                  {errors.symptom_id && (
                    <div className="text-danger d-block mb-2">{errors.symptom_id}</div>
                  )}

                </div>

                <div className="col-xl-12 mb-3">
                  <label className="form-label">Medicine Name<span className="text-danger">*</span>
                  </label>
                  <Select
                    name="medicines"
                    options={medicines.map((m) => ({
                      value: String(m.medicine_id),
                      label: m.medicine_name,
                    }))}
                    value={medicines
                      .map((m) => ({
                        value: String(m.medicine_id),
                        label: m.medicine_name,
                      }))
                      .find((m) => String(m.value) === mapping.medicine_id)}
                    onChange={(selectedOption) =>
                      setMapping({
                        ...mapping,
                        medicine_id: selectedOption.value,
                      })
                    }
                    placeholder="Select Medicine"
                    //required
                  />
                  {errors.medicine_id && (
                    <div className="text-danger d-block mb-2">{errors.medicine_id}</div>
                  )}
                </div>

                <div className="col-xl-6 mb-3">
                  <label className="form-label">Status</label>
                  <Select
                    name="status"
                    options={[
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                    ]}
                    value={
                      mapping.status
                        ? { value: mapping.status, label: mapping.status }
                        : null
                    }
                    onChange={(selectedOption) =>
                      setMapping({
                        ...mapping,
                        status: selectedOption?.value || "", // Handles clear (if `isClearable` is enabled)
                      })
                    }
                    isClearable
                    placeholder="Select Status"
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minHeight: "50px",
                        height: "50px",
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        lineHeight: "50px",
                      }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        padding: "4px",
                      }),
                      indicatorSeparator: () => ({
                        display: "none",
                      }),
                    }}
                  />

                  {errors.status && (
                    <div className="text-danger d-block mb-2">{errors.status}</div>
                  )}
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader size="sm" />
                    <span className="ms-2">{isEdit ? "Updating" : "Saving"}...</span>
                  </>
                ) : (
                  <>{isEdit ? "Update" : "Save"} Symptom Medicine Mapping</>
                )}
              </button>

            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddSymptomMedicineMapping;